/** QuickPlan — Vue 3D : perspective ou vues orthogonales (face, dessus, droite),
 *  sélection au clic et par zone, gizmo déplacer / tourner avec accroche (grille,
 *  angle, ports), double-clic = ouvrir / fermer, outil de pose de fixations. */
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, OrthographicCamera, TransformControls } from '@react-three/drei';
import { useProjectStore } from '../store/useProjectStore.js';
import InstanceNode, { objectRegistry, frameRegistry } from './InstanceMesh.jsx';
import { projectBounds, instancePorts, hiddenSet, frames, resolvedOf } from '../geom.js';
import { bboxInFrame } from '../../shared/frames.js';
import { dist, sub } from '../../shared/resolve.js';
import ViewBar from './ViewBar.jsx';

// Z vers le haut, comme en CAO (toutes les caméras créées ensuite en héritent).
THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

const DEG = Math.PI / 180;
const round = (v, q = 100) => Math.round(v * q) / q;
const PORT_SNAP_MM = 25;

/** Cadrage : place la caméra active sur l'encombrement du projet selon la vue. */
function CameraRig() {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls);
  const size = useThree((s) => s.size);
  const fitNonce = useProjectStore((s) => s.fitNonce);
  const view = useProjectStore((s) => s.view);

  useEffect(() => {
    if (!controls) return;
    const st = useProjectStore.getState();
    const bb = (st.project && projectBounds(st.project, st.lookup())) ?? { min: [-500, -500, 0], max: [500, 500, 600] };
    const c = new THREE.Vector3(...bb.min.map((v, k) => (v + bb.max[k]) / 2));
    const s = bb.max.map((v, k) => Math.max(1, v - bb.min[k]));
    const radius = Math.max(200, 0.5 * Math.hypot(...s));

    if (camera.isPerspectiveCamera) {
      const d = (radius / Math.sin((camera.fov * DEG) / 2)) * 1.05;
      const dir = new THREE.Vector3(1, -1.5, 0.9).normalize();
      camera.up.set(0, 0, 1);
      camera.position.copy(c).addScaledVector(dir, d);
      camera.near = Math.max(1, d / 200);
      camera.far = d * 200;
    } else {
      const D = radius * 4 + 2000;
      let span;
      if (view === 'top') { camera.up.set(0, 1, 0); camera.position.set(c.x, c.y, c.z + D); span = [s[0], s[1]]; }
      else if (view === 'right') { camera.up.set(0, 0, 1); camera.position.set(c.x + D, c.y, c.z); span = [s[1], s[2]]; }
      else { camera.up.set(0, 0, 1); camera.position.set(c.x, c.y - D, c.z); span = [s[0], s[2]]; }
      camera.near = 0.1;
      camera.far = D * 3;
      camera.zoom = Math.min(size.width / span[0], size.height / span[1]) * 0.8;
    }
    camera.lookAt(c);
    camera.updateProjectionMatrix();
    controls.target.copy(c);
    controls.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitNonce, camera, controls]);
  return null;
}

/** Gizmo de transformation sur l'instance sélectionnée (sélection simple). Il agit sur
 *  le placement propre, dans le repère du parent : les pièces portées suivent. */
function Gizmo({ project }) {
  const selection = useProjectStore((s) => s.selection);
  const mode = useProjectStore((s) => s.gizmo);
  const tool = useProjectStore((s) => s.tool);
  const busy = useProjectStore((s) => !!s.ai.job);
  const [obj, setObj] = useState(null);
  const id = selection.length === 1 ? selection[0] : null;

  useEffect(() => { setObj(id ? objectRegistry.get(id) ?? null : null); }, [id, project]);
  if (!obj || busy || tool !== 'select') return null;
  const st = useProjectStore.getState;

  const read = (p) => {
    const inst = p.instances.find((i) => i.id === id);
    if (!inst) return;
    inst.pos = [obj.position.x, obj.position.y, obj.position.z].map((v) => round(v));
    inst.rot = [obj.rotation.x, obj.rotation.y, obj.rotation.z].map((v) => round(v / DEG, 1000) + 0);
  };

  const snapToPorts = () => {
    const { project: p, lookup } = st();
    if (!p.settings.snapPorts) return;
    const lk = lookup();
    const inst = p.instances.find((i) => i.id === id);
    if (!inst) return;
    const mine = instancePorts(inst, lk, p);
    if (!mine.length) return;
    const skip = new Set([id]);
    for (const x of p.instances) if (x.parentId === id) skip.add(x.id);
    let best = null;
    for (const other of p.instances) {
      if (skip.has(other.id) || other.hidden) continue;
      for (const op of instancePorts(other, lk, p)) {
        for (const mp of mine) {
          const d = dist(op.pos, mp.pos);
          if (d > 0.01 && d < PORT_SNAP_MM && (!best || d < best.d)) best = { d, delta: sub(op.pos, mp.pos), label: `${other.name || other.componentId} · ${op.id}` };
        }
      }
    }
    if (!best) return;
    // Décalage monde → repère du parent (calcul three.js : mêmes conventions que le modèle).
    const world = obj.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(...best.delta));
    const local = obj.parent.worldToLocal(world);
    st().applyTransient((d) => {
      const i = d.instances.find((x) => x.id === id);
      i.pos = [local.x, local.y, local.z].map((v) => round(v));
    });
    st().setStatus(`Accroché au port ${best.label}`);
  };

  return (
    <TransformControls
      object={obj}
      mode={mode}
      size={0.8}
      space={mode === 'rotate' ? 'local' : 'world'}
      translationSnap={project.settings.grid}
      rotationSnap={project.settings.snapAngle * DEG}
      onMouseDown={() => st().beginTransient()}
      onObjectChange={() => st().applyTransient(read)}
      onMouseUp={() => { snapToPorts(); st().commitTransient(); }}
    />
  );
}

/** Sélection par zone : Maj + glisser (ou mode Zone). Gauche → droite = entièrement
 *  dans le cadre ; droite → gauche = touché par le cadre (comme en CAO). */
function BoxSelect({ onRect }) {
  const gl = useThree((s) => s.gl);
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls);

  useEffect(() => {
    const el = gl.domElement;
    let start = null;
    const pos = (e) => { const r = el.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
    const down = (e) => {
      const st = useProjectStore.getState();
      if (e.button !== 0 || !(e.shiftKey || st.boxMode) || st.tool !== 'select') return;
      start = pos(e);
      if (controls) controls.enabled = false;
      e.stopPropagation();
    };
    const move = (e) => {
      if (!start) return;
      const p = pos(e);
      onRect({ x0: start[0], y0: start[1], x1: p[0], y1: p[1] });
    };
    const up = (e) => {
      if (!start) return;
      const p = pos(e);
      const s = start;
      start = null;
      onRect(null);
      if (controls) controls.enabled = true;
      if (Math.abs(p[0] - s[0]) < 4 && Math.abs(p[1] - s[1]) < 4) return;
      const st = useProjectStore.getState();
      const proj = st.project;
      const lookup = st.lookup();
      const F = frames(proj, lookup);
      const hid = hiddenSet(proj);
      const r = el.getBoundingClientRect();
      const [xa, xb] = [Math.min(s[0], p[0]), Math.max(s[0], p[0])];
      const [ya, yb] = [Math.min(s[1], p[1]), Math.max(s[1], p[1])];
      const crossing = p[0] < s[0];
      const hits = [];
      const v = new THREE.Vector3();
      for (const inst of proj.instances) {
        if (hid.has(inst.id)) continue;
        const res = resolvedOf(inst, lookup);
        if (!res) continue;
        const bb = bboxInFrame(F.get(inst.id), res);
        let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
        for (const x of [bb.min[0], bb.max[0]]) for (const y of [bb.min[1], bb.max[1]]) for (const z of [bb.min[2], bb.max[2]]) {
          v.set(x, y, z).project(camera);
          const sx = ((v.x + 1) / 2) * r.width;
          const sy = ((1 - v.y) / 2) * r.height;
          minX = Math.min(minX, sx); maxX = Math.max(maxX, sx); minY = Math.min(minY, sy); maxY = Math.max(maxY, sy);
        }
        const inside = minX >= xa && maxX <= xb && minY >= ya && maxY <= yb;
        const touch = maxX >= xa && minX <= xb && maxY >= ya && minY <= yb;
        if (crossing ? touch : inside) hits.push(inst.id);
      }
      const add = e.ctrlKey || e.metaKey;
      st.setSelection(add ? [...new Set([...st.selection, ...hits])] : hits);
      st.markBoxed();
    };
    el.addEventListener('pointerdown', down, true);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      el.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [gl, camera, controls, onRect]);
  return null;
}

function Ports({ project, lookup, selection, hidden }) {
  const sel = new Set(selection);
  const geo = useMemo(() => new THREE.SphereGeometry(6, 10, 8), []);
  const matA = useMemo(() => new THREE.MeshBasicMaterial({ color: '#e3b566', depthTest: false, transparent: true }), []);
  const matS = useMemo(() => new THREE.MeshBasicMaterial({ color: '#8fd0a3', depthTest: false, transparent: true }), []);
  const out = [];
  for (const inst of project.instances) {
    if (hidden.has(inst.id)) continue;
    for (const p of instancePorts(inst, lookup, project)) {
      out.push(<mesh key={`${inst.id}:${p.id}`} geometry={geo} material={sel.has(inst.id) ? matS : matA} position={p.pos} renderOrder={10} raycast={() => null} />);
      if (out.length > 3000) return out;
    }
  }
  return out;
}

export default function Viewer3D() {
  const project = useProjectStore((s) => s.project);
  const library = useProjectStore((s) => s.library);
  const selection = useProjectStore((s) => s.selection);
  const view = useProjectStore((s) => s.view);
  const showPorts = useProjectStore((s) => s.showPorts);
  const showEdges = useProjectStore((s) => s.showEdges);
  const showFixings = useProjectStore((s) => s.showFixings);
  const tool = useProjectStore((s) => s.tool);
  const boxMode = useProjectStore((s) => s.boxMode);
  const select = useProjectStore((s) => s.select);
  const [rect, setRect] = useState(null);
  const lookup = useMemo(() => useProjectStore.getState().lookup(), [project, library]); // eslint-disable-line react-hooks/exhaustive-deps
  const onRect = useRef((r) => setRect(r)).current;
  if (!project) return null;

  const hidden = hiddenSet(project);
  const ids = new Set(project.instances.map((i) => i.id));
  const kids = new Map();
  const roots = [];
  for (const i of project.instances) {
    if (i.parentId && ids.has(i.parentId)) {
      if (!kids.has(i.parentId)) kids.set(i.parentId, []);
      kids.get(i.parentId).push(i);
    } else roots.push(i);
  }
  const st = useProjectStore.getState;
  const ctx = {
    lookup,
    selSet: new Set(selection),
    hidden,
    showEdges,
    showFixings,
    tool,
    onPick: select,
    onDouble: (id) => st().toggleOpen(id),
    onFix: (id, point, normal) => {
      const frame = frameRegistry.get(id);
      if (!frame) return;
      frame.updateWorldMatrix(true, false);
      const local = frame.worldToLocal(point.clone());
      const inv = new THREE.Matrix3().setFromMatrix4(frame.matrixWorld).invert();
      const n = normal.clone().applyMatrix3(inv).normalize();
      st().placeFixation(id, [local.x, local.y, local.z], [n.x, n.y, n.z]);
    },
  };

  return (
    <div className={`viewer ${tool === 'fixation' ? 'tool-fix' : ''} ${boxMode ? 'tool-box' : ''}`}>
      <Canvas dpr={[1, 2]} onPointerMissed={(e) => { if (e.button === 0 && !st().recentlyBoxed()) select(null); }}>
        <color attach="background" args={['#101215']} />
        {view === 'persp'
          ? <PerspectiveCamera key="persp" makeDefault fov={40} position={[2500, -3500, 2200]} near={5} far={500000} />
          : <OrthographicCamera key={`ortho-${view}`} makeDefault position={[0, -10000, 0]} near={0.1} far={500000} zoom={0.2} />}
        <OrbitControls key={`ctl-${view}`} makeDefault enableRotate={view === 'persp'} screenSpacePanning />
        <CameraRig />
        <BoxSelect onRect={onRect} />
        <hemisphereLight args={['#ffffff', '#3a4255', 0.9]} />
        <directionalLight position={[3000, -4000, 6000]} intensity={1.6} />
        <directionalLight position={[-4000, 3000, 2000]} intensity={0.5} />
        <gridHelper args={[20000, 400, '#1c2027', '#1c2027']} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, -0.5]} />
        <gridHelper args={[20000, 20, '#2a3140', '#2a3140']} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, -0.4]} />
        <axesHelper args={[400]} />
        {roots.map((inst) => <InstanceNode key={inst.id} inst={inst} kids={kids} ctx={ctx} />)}
        {showPorts && <Ports project={project} lookup={lookup} selection={selection} hidden={hidden} />}
        <Gizmo project={project} />
      </Canvas>
      {rect && (
        <div
          className={`box-rect ${rect.x1 < rect.x0 ? 'crossing' : ''}`}
          style={{ left: Math.min(rect.x0, rect.x1), top: Math.min(rect.y0, rect.y1), width: Math.abs(rect.x1 - rect.x0), height: Math.abs(rect.y1 - rect.y0) }}
        />
      )}
      <ViewBar />
    </div>
  );
}
